import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, X, Send } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

const inputCls = "mt-1.5 w-full border border-line bg-ink px-3 py-2.5 text-sm text-white outline-none transition-colors focus:border-accent";

const STATUS_LABELS = { new: "未対応", in_progress: "対応中", done: "対応済" };
const STATUS_COLORS = { new: "text-accent", in_progress: "text-yellow-400", done: "text-green-400" };

export default function Dashboard() {
  const [inquiries, setInquiries] = useState([]);
  const [worksCount, setWorksCount] = useState(0);
  const [propsCount, setPropsCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/admin/inquiries"), api.get("/admin/works"), api.get("/admin/properties")])
      .then(([i, w, p]) => {
        setInquiries(i.data);
        setWorksCount(w.data.length);
        setPropsCount(p.data.length);
      })
      .catch(() => toast.error("データの取得に失敗しました"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status });
      setInquiries((prev) => prev.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success("ステータスを更新しました");
    } catch {
      toast.error("更新に失敗しました");
    }
  };

  const [replyTo, setReplyTo] = useState(null);
  const [replyForm, setReplyForm] = useState({ subject: "", body: "" });
  const [sending, setSending] = useState(false);

  const openReply = (inq) => {
    setReplyForm({
      subject: "【株式会社ネオ建設】お問い合わせへのご回答",
      body: `${inq.name} 様\n\nお問い合わせありがとうございます。\n株式会社ネオ建設です。\n\n`,
    });
    setReplyTo(inq);
  };

  const sendReply = async () => {
    setSending(true);
    try {
      await api.post(`/admin/inquiries/${replyTo.id}/reply`, replyForm);
      toast.success("返信メールを送信しました");
      setReplyTo(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const newCount = inquiries.filter((i) => i.status === "new").length;

  const stats = [
    { label: "未対応の問い合わせ", value: newCount, accent: true },
    { label: "総問い合わせ数", value: inquiries.length },
    { label: "施工実績", value: worksCount },
    { label: "不動産物件", value: propsCount },
  ];

  return (
    <div data-testid="admin-dashboard">
      <h1 className="font-mincho text-2xl font-bold text-white">ダッシュボード</h1>
      <p className="mt-1 font-mono text-[10px] tracking-[0.3em] text-gray-500">INQUIRIES & OVERVIEW</p>

      <div className="mt-8 grid grid-cols-2 gap-px border border-line bg-line lg:grid-cols-4">
        {stats.map((s) => (
          <div key={s.label} className="bg-panel p-6">
            <p className={`font-display text-4xl font-extrabold ${s.accent ? "text-accent" : "text-white"}`}>{s.value}</p>
            <p className="mt-2 text-xs text-gray-500">{s.label}</p>
          </div>
        ))}
      </div>

      <h2 className="mt-12 font-mincho text-lg font-bold text-white">お問い合わせ一覧</h2>
      {loading ? (
        <p className="mt-6 font-mono text-sm tracking-[0.3em] text-gray-500">LOADING...</p>
      ) : inquiries.length === 0 ? (
        <p data-testid="inquiries-empty" className="mt-6 text-sm text-gray-500">
          お問い合わせはまだありません。
        </p>
      ) : (
        <div data-testid="inquiries-table" className="mt-6 overflow-x-auto border border-line">
          <table className="w-full text-left text-sm lg:min-w-[900px]">
            <thead>
              <tr className="border-b border-line bg-coal font-mono text-[10px] tracking-[0.2em] text-gray-500">
                <th className="hidden px-4 py-3 lg:table-cell">受信日時</th>
                <th className="px-4 py-3">種別</th>
                <th className="px-4 py-3">氏名</th>
                <th className="hidden px-4 py-3 lg:table-cell">会社</th>
                <th className="hidden px-4 py-3 lg:table-cell">連絡先</th>
                <th className="hidden px-4 py-3 lg:table-cell">内容</th>
                <th className="px-4 py-3">状態</th>
              </tr>
            </thead>
            <tbody>
              {inquiries.map((inq) => (
                <tr key={inq.id} data-testid={`inquiry-row-${inq.id}`} className="border-b border-line bg-panel align-top last:border-b-0">
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-400">{new Date(inq.created_at).toLocaleString("ja-JP")}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-300">{inq.category}</td>
                  <td className="px-4 py-3 text-xs font-bold text-white">
                    <span className="whitespace-nowrap">{inq.name}</span>
                    <button
                      data-testid={`inquiry-reply-${inq.id}`}
                      onClick={() => openReply(inq)}
                      className="mt-2 flex items-center gap-1.5 bg-accent px-3 py-1.5 font-display text-xs font-bold tracking-[0.1em] text-ink transition-colors hover:bg-accent-hover"
                    >
                      <Mail className="h-3.5 w-3.5" />
                      メールで返信
                    </button>
                    {(inq.replies || []).length > 0 && (
                      <p className="mt-1.5 font-mono text-[9px] font-normal tracking-[0.15em] text-accent">返信済 {inq.replies.length}件</p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">{inq.company || "—"}</td>
                  <td className="px-4 py-3 text-xs text-gray-400">
                    {inq.email}
                    {inq.phone && <br />}
                    {inq.phone}
                  </td>
                  <td className="max-w-xs px-4 py-3 text-xs text-gray-400">
                    <span className="line-clamp-2">{inq.message}</span>
                  </td>
                  <td className="px-4 py-3">
                    <select
                      data-testid={`inquiry-status-${inq.id}`}
                      value={inq.status}
                      onChange={(e) => updateStatus(inq.id, e.target.value)}
                      className={`border border-line bg-ink px-2 py-1.5 text-xs outline-none ${STATUS_COLORS[inq.status] || "text-gray-300"}`}
                    >
                      {Object.entries(STATUS_LABELS).map(([v, l]) => (
                        <option key={v} value={v}>
                          {l}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {replyTo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/80 p-4 backdrop-blur-sm">
          <div data-testid="reply-modal" className="max-h-[90vh] w-full max-w-xl overflow-y-auto border border-line bg-coal p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-mincho text-lg font-bold text-white">お問い合わせに返信</h2>
              <button type="button" data-testid="reply-modal-close" onClick={() => setReplyTo(null)} className="text-gray-500 transition-colors hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-6 border border-line bg-ink p-4 text-xs text-gray-400">
              <p>
                宛先: <span className="font-bold text-white">{replyTo.name}</span> 様 ＜{replyTo.email}＞
              </p>
              <p className="mt-2 text-gray-500">
                元のお問い合わせ（{replyTo.category}）: {replyTo.message.slice(0, 80)}
                {replyTo.message.length > 80 ? "…" : ""}
              </p>
            </div>
            {(replyTo.replies || []).length > 0 && (
              <p className="mt-3 font-mono text-[10px] tracking-[0.2em] text-accent">
                返信済み {replyTo.replies.length}件（最新: {new Date(replyTo.replies[replyTo.replies.length - 1].sent_at).toLocaleString("ja-JP")}）
              </p>
            )}
            <label className="mt-5 block">
              <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">件名</span>
              <input
                data-testid="reply-subject-input"
                value={replyForm.subject}
                onChange={(e) => setReplyForm((f) => ({ ...f, subject: e.target.value }))}
                className={inputCls}
              />
            </label>
            <label className="mt-4 block">
              <span className="font-mono text-[10px] tracking-[0.3em] text-gray-500">本文</span>
              <textarea
                data-testid="reply-body-input"
                rows={8}
                value={replyForm.body}
                onChange={(e) => setReplyForm((f) => ({ ...f, body: e.target.value }))}
                className={`resize-y ${inputCls}`}
              />
            </label>
            <button
              data-testid="reply-send-button"
              onClick={sendReply}
              disabled={sending || !replyForm.subject || !replyForm.body}
              className="mt-6 flex w-full items-center justify-center gap-2 bg-accent py-3 font-display text-sm font-bold tracking-[0.2em] text-ink transition-colors hover:bg-accent-hover disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {sending ? "送信中..." : "メールを送信する"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
